import { Button, ColumnTable, Icon, addToast } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { enqueueStudentCertificate } from '../api';
import { Col, Row } from 'reactstrap';

// Efecto spinner del ícono de actualizar
if (
  typeof document !== 'undefined' &&
  !document.getElementById('ce-spin-keyframes')
) {
  const style = document.createElement('style');
  style.id = 'ce-spin-keyframes';
  style.textContent =
    '@keyframes ce-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

const STATUS_LABELS: Record<string, string> = {
  regular: 'En curso',
  egresado: 'Finalizado',
  titulado: 'Titulado',
};

interface UseCertificatesStudentsColumnsOptions {
  onCheckSingle?: (enrollmentId: number) => void;
  checkingId?: number | null;
  onAfterEnqueue?: () => void | Promise<void>;
}

export const useCertificatesStudentsColumns = (
  options: UseCertificatesStudentsColumnsOptions = {},
): ColumnTable[] => {
  const { onCheckSingle, checkingId, onAfterEnqueue } = options;
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_CERTIFICATES,
  );
  const prefix = 'students.table';

  const handleGenerateCertificate = useCallback(
    async (row: { ceAcademicOfferId: number; studentId: number }) => {
      try {
        const { error } = await enqueueStudentCertificate(
          row.ceAcademicOfferId,
          row.studentId,
        );

        if (!error) {
          addToast({
            icon: 'success',
            color: 'success',
            text: t('students.table.certificateEnqueued'),
          });
          await onAfterEnqueue?.();
        } else {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t('students.table.certificateEnqueueError'),
          });
        }
      } catch {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('students.table.certificateEnqueueError'),
        });
      }
    },
    [onAfterEnqueue, t],
  );

  const openUrl = useCallback((url?: string | null) => {
    if (!url) {
      return;
    }
    window.open(url.trim(), '_blank');
  }, []);

  return useMemo(
    () => [
      {
        columnName: 'idType',
        headerText: t(`${prefix}.columns.idType`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => row.idType,
      },
      {
        columnName: 'id',
        headerText: t(`${prefix}.columns.id`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => row.id,
      },
      {
        columnName: 'firstName',
        headerText: t(`${prefix}.columns.firstName`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => row.firstName,
      },
      {
        columnName: 'lastNamePaternal',
        headerText: t(`${prefix}.columns.lastNamePaternal`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return `${row.lastNamePaternal} ${row.lastNameMaternal}`;
        },
      },
      {
        columnName: 'grade',
        headerText: t(`${prefix}.columns.grade`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => row.grade ?? '-',
      },
      {
        columnName: 'phone',
        headerText: t(`${prefix}.columns.phone`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => row.phone,
      },
      {
        columnName: 'email',
        headerText: t(`${prefix}.columns.email`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => row.email,
      },
      {
        columnName: 'finalizationDate',
        headerText: t(`${prefix}.columns.finalizationDate`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.completionDate
            ? new Date(row.completionDate).toLocaleDateString('es-CL')
            : '-',
      },
      {
        columnName: 'finalizationStatus',
        headerText: t(`${prefix}.columns.finalizationStatus`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) =>
          STATUS_LABELS[row.enrollmentStatus] ?? row.enrollmentStatus ?? '-',
      },
      {
        columnName: 'updateStatus',
        headerText: t(`${prefix}.columns.updateStatus`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const isRegular = row.enrollmentStatus === 'regular';
          const isChecking = checkingId === row.enrollmentId;
          return (
            <button
              type="button"
              disabled={!isRegular || isChecking}
              onClick={() => onCheckSingle?.(row.enrollmentId)}
              style={{
                background: 'none',
                border: 'none',
                cursor: isRegular && !isChecking ? 'pointer' : 'not-allowed',
                opacity: isRegular ? 1 : 0.4,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  animation: isChecking
                    ? 'ce-spin 0.8s linear infinite'
                    : undefined,
                }}
              >
                <Icon name="refresh" size="25px" />
              </span>
            </button>
          );
        },
      },
      {
        columnName: 'generateCertificate',
        headerText: t(`${prefix}.columns.generateCertificate`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          if (row.certificateGenerating) {
            return (
              <Button text="Generando..." size="sm" fullwidth disabled={true} />
            );
          }

          if (row.certificateLastGenerated?.url) {
            return (
              <Row xs={12} className="justify-content-between">
                <Col xs={12} xl={6} className="mb-2 mb-xl-0">
                  <Button
                    text="Descargar"
                    size="sm"
                    fullwidth
                    onClick={() => openUrl(row.certificateLastGenerated?.url)}
                  />
                </Col>
                <Col xs={12} xl={6} className="mb-2 mb-xl-0">
                  <Button
                    text="Volver a generar"
                    size="sm"
                    fullwidth
                    onClick={() =>
                      handleGenerateCertificate({
                        ceAcademicOfferId: row.ceAcademicOfferId,
                        studentId: row.studentId,
                      })
                    }
                  />
                </Col>
              </Row>
            );
          }

          return (
            <Button
              text="Generar"
              size="sm"
              fullwidth
              onClick={() =>
                handleGenerateCertificate({
                  ceAcademicOfferId: row.ceAcademicOfferId,
                  studentId: row.studentId,
                })
              }
            />
          );
        },
      },
    ],
    [t, onCheckSingle, checkingId, openUrl, handleGenerateCertificate],
  );
};
